/* eslint-disable no-console */
import { useState, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { Header } from "../Header";
import AsignarRecibidaModal from "./AsignarRecibidaModal";
import VincularEmitidaModal from "./VincularEmitidaModal";
import styles from "./ConciliacionSII.module.scss";

// ─── Helpers ─────────────────────────────────────────────

const formatRut = (rut) => {
  if (!rut) return "-";
  const [cuerpo, dv] = rut.split("-");
  if (!dv) return rut;
  return `${Number(cuerpo).toLocaleString("es-CL")}-${dv}`;
};

const formatMonto = (monto) => {
  if (monto == null) return "-";
  return `$ ${Number(monto).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [, month, day] = fechaStr.split("-");
  return `${day}/${month}`;
};

const getPeriodoActual = () => `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const getPeriodoLabel = (periodo) => {
  if (!periodo) return "";
  const [year, month] = periodo.split("-");
  return `${MESES[parseInt(month, 10) - 1]} ${year}`;
};

const generarPeriodos = () => {
  const periodos = [];
  const now = new Date();
  const MIN_PERIODO = "2026-01";
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (val < MIN_PERIODO) break;
    periodos.push(val);
  }
  return periodos;
};

const TIPO_DTE_LABEL = {
  33: "Factura",
  34: "Exenta",
  39: "Boleta",
  41: "Boleta exenta",
  56: "Nota débito",
  61: "Nota crédito",
  110: "Exportación",
  111: "Liquidación exp.",
  112: "Nota débito exp.",
};

// ─── Componente principal ────────────────────────────────

const ConciliacionSII = () => {
  const { pais } = useAuthContext();
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";

  const [activeTab, setActiveTab] = useState("todas");
  const [periodo, setPeriodo] = useState(getPeriodoActual());
  const [loading, setLoading] = useState(false);
  const [emitidas, setEmitidas] = useState([]);
  const [recibidas, setRecibidas] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [modalEmitida, setModalEmitida] = useState(null);
  const [modalRecibida, setModalRecibida] = useState(null);
  const [importando, setImportando] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const periodos = generarPeriodos();

  // ─── Carga de datos ────────────────────────────────────

  const cargarDatos = useCallback(async () => {
    if (!pais || pais === "Region") return;
    setLoading(true);
    try {
      const [em, rec] = await Promise.all([
        getFacturasEmitidas(pais, { periodo }),
        getFacturasRecibidas(pais, { periodo }),
      ]);
      setEmitidas(em);
      setRecibidas(rec);
    } catch (err) {
      console.error("Error cargando conciliación SII:", err);
      toast.error(`Error cargando datos del ${enteFiscal}`);
    } finally {
      setLoading(false);
    }
  }, [pais, periodo, enteFiscal]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // ─── Importar período ──────────────────────────────────

  const importarPeriodo = useCallback(async () => {
    if (!periodo) return;
    setImportando(true);
    setImportResult(null);
    try {
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/sii-sync?periodo=${periodo}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setImportResult(data);
      toast.success(
        `Importado: ${data.emitidas?.nuevas || 0} emitidas + ${data.recibidas?.nuevas || 0} recibidas nuevas`
      );
      await cargarDatos();
    } catch (err) {
      console.error("Error importando SII:", err);
      toast.error(`Error importando desde ${enteFiscal}`);
    } finally {
      setImportando(false);
    }
  }, [periodo, cargarDatos, enteFiscal]);

  // ─── Datos filtrados ───────────────────────────────────

  const filtrar = (arr) => {
    let result = arr;
    if (filtroEstado) result = result.filter((f) => f.estado_vinculacion === filtroEstado);
    if (busqueda) {
      const q = busqueda.toLowerCase();
      result = result.filter((f) => {
        const razon = (f.razon_social_receptor || f.razon_social_emisor || "").toLowerCase();
        const rut = (f.rut_receptor || f.rut_emisor || "").toLowerCase();
        return razon.includes(q) || rut.includes(q);
      });
    }
    return result;
  };

  // Separar notas de crédito/débito (61, 56, 111, 112)
  const esNC = (f) => [61, 56, 111, 112].includes(f.tipo_dte);
  const emitidasSinNC = emitidas.filter((f) => !esNC(f));
  const recibidasSinNC = recibidas.filter((f) => !esNC(f));
  const notasCredito = [...emitidas.filter(esNC), ...recibidas.filter(esNC)];

  const datosTabla = useMemo(() => {
    const mapDir = (arr, dir) => filtrar(arr).map((f) => ({ ...f, _dir: dir }));
    let result;
    if (activeTab === "emitidas") result = mapDir(emitidasSinNC, "emitida");
    else if (activeTab === "recibidas") result = mapDir(recibidasSinNC, "recibida");
    else if (activeTab === "nc") result = filtrar(notasCredito).map((f) => ({ ...f, _dir: esNC(f) ? "nc" : "nc" }));
    else {
      const ncEmitidas = filtrar(emitidas.filter(esNC)).map((f) => ({ ...f, _dir: "emitida" }));
      const ncRecibidas = filtrar(recibidas.filter(esNC)).map((f) => ({ ...f, _dir: "recibida" }));
      result = [
        ...mapDir(emitidasSinNC, "emitida"),
        ...mapDir(recibidasSinNC, "recibida"),
        ...ncEmitidas,
        ...ncRecibidas,
      ];
    }
    result.sort((a, b) => (b.fecha_emision || "").localeCompare(a.fecha_emision || ""));
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, emitidas, recibidas, filtroEstado, busqueda]);

  const totalEmitidas = useMemo(
    () => emitidasSinNC.reduce((s, f) => s + Number(f.monto_total || 0), 0),
    [emitidasSinNC]
  );
  const totalRecibidas = useMemo(
    () => recibidasSinNC.reduce((s, f) => s + Number(f.monto_total || 0), 0),
    [recibidasSinNC]
  );
  const totalNC = useMemo(() => notasCredito.reduce((s, f) => s + Number(f.monto_total || 0), 0), [notasCredito]);

  const ivaDebito = useMemo(() => emitidas.reduce((s, f) => s + Number(f.monto_iva || 0), 0), [emitidas]);
  const ivaCredito = useMemo(() => recibidas.reduce((s, f) => s + Number(f.monto_iva || 0), 0), [recibidas]);
  const saldoIVA = ivaDebito - ivaCredito;

  // ─── Render ────────────────────────────────────────────

  const centerControls = (
    <div className={styles.headerCenter}>
      <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className={styles.headerSelect}>
        {periodos.map((p) => (
          <option key={p} value={p}>
            {getPeriodoLabel(p)}
          </option>
        ))}
      </select>
      <div className={styles.toggleGroup}>
        {["emitidas", "recibidas", "nc", "todas"].map((tab) => (
          <button
            key={tab}
            className={`${styles.toggleBtn} ${activeTab === tab ? styles.toggleActive : ""}`}
            onClick={() => {
              setActiveTab(tab);
              setFiltroEstado("");
            }}
          >
            {tab === "emitidas"
              ? "Emitidas"
              : tab === "recibidas"
                ? "Recibidas"
                : tab === "nc"
                  ? "Notas Crédito"
                  : "Todas"}
          </button>
        ))}
      </div>
      <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className={styles.headerSelect}>
        <option value="">Todos</option>
        <option value="pendiente">Pendientes</option>
        <option value="vinculada">Vinculadas</option>
        <option value="ignorada">Ignoradas</option>
      </select>
      <input
        type="text"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar RUT o razón social..."
        className={styles.headerSearch}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <Header title={`Conciliación ${enteFiscal}`} centerContent={centerControls}>
        <button className={styles.btnImportar} onClick={importarPeriodo} disabled={importando}>
          {importando ? "..." : "Sincronizar"}
        </button>
      </Header>

      <div className={styles.content}>
        {/* Totales */}
        <div className={styles.totalBar}>
          <span>
            Emitidas: <strong style={{ color: "#27ae60" }}>{formatMonto(totalEmitidas)}</strong>
            {" | "}
            Recibidas: <strong style={{ color: "#c0392b" }}>{formatMonto(totalRecibidas)}</strong>
            {totalNC > 0 && (
              <>
                {" | "}
                NC: <strong style={{ color: "#e67e22" }}>{formatMonto(totalNC)}</strong>
              </>
            )}
            {" | "}
            {"IVA Ventas: "}
            <strong>{formatMonto(ivaDebito)}</strong>
            {" | IVA Compras: "}
            <strong>{formatMonto(ivaCredito)}</strong>
            {" | IVA Saldo: "}
            <strong style={{ color: saldoIVA > 0 ? "#c0392b" : "#27ae60" }}>{formatMonto(saldoIVA)}</strong>
          </span>
          <span className={styles.totalCount}>
            {datosTabla.length} documento{datosTabla.length === 1 ? "" : "s"}
            {" · "}
            {(() => {
              const pend = datosTabla.filter((f) => f.estado_vinculacion === "pendiente").length;
              return pend > 0 ? (
                <strong style={{ color: "#e67e22" }}>
                  {pend} pendiente{pend > 1 ? "s" : ""}
                </strong>
              ) : (
                <strong style={{ color: "#27ae60" }}>Todo vinculado</strong>
              );
            })()}
          </span>
        </div>

        {/* Resultado importación */}
        {importResult && (
          <div className={styles.importResult}>
            Importación completada — Emitidas: <strong>{importResult.emitidas?.nuevas || 0} nuevas</strong> | Recibidas:{" "}
            <strong>{importResult.recibidas?.nuevas || 0} nuevas</strong>,{" "}
            {importResult.recibidas?.matched_proveedores || 0} matched
          </div>
        )}

        {loading && <div className={styles.empty}>Cargando...</div>}

        {/* Tabla unificada */}
        {!loading && datosTabla.length === 0 && (
          <div className={styles.empty}>No hay facturas para {getPeriodoLabel(periodo)}</div>
        )}

        {!loading && datosTabla.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                {activeTab === "todas" && <th>Movimiento</th>}
                <th className={styles.alignLeft}>Tipo</th>
                <th>Fecha</th>
                <th>Folio</th>
                <th>RUT</th>
                <th className={styles.razonCol}>Razón Social</th>
                <th className={styles.montoCol}>Neto</th>
                <th className={styles.montoCol}>IVA</th>
                <th className={styles.montoCol}>Total</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {datosTabla.map((f) => (
                <tr key={`${f._dir}-${f.id}`}>
                  {activeTab === "todas" && (
                    <td>
                      <span
                        className={`${styles.dirChip} ${
                          esNC(f) ? styles.dirNota : f._dir === "emitida" ? styles.dirIngreso : styles.dirCosto
                        }`}
                      >
                        {esNC(f) ? "N. Crédito" : f._dir === "emitida" ? "Venta" : "Costo"}
                      </span>
                    </td>
                  )}
                  <td className={styles.alignLeft}>{TIPO_DTE_LABEL[f.tipo_dte] || f.tipo_dte}</td>
                  <td className={styles.centered}>{formatFecha(f.fecha_emision)}</td>
                  <td className={styles.centered}>{f.folio}</td>
                  <td className={styles.centered}>{formatRut(f._dir === "emitida" ? f.rut_receptor : f.rut_emisor)}</td>
                  <td className={styles.razonCol}>
                    {f._dir === "emitida" ? f.razon_social_receptor : f.razon_social_emisor}
                  </td>
                  <td className={styles.montoCol}>
                    {Number(f.monto_neto) === 0 && Number(f.monto_exento) > 0
                      ? formatMonto(f.monto_exento)
                      : formatMonto(f.monto_neto)}
                  </td>
                  <td className={styles.montoCol}>{formatMonto(f.monto_iva)}</td>
                  <td className={`${styles.montoCol} ${styles.montoTotal}`}>{formatMonto(f.monto_total)}</td>
                  <td className={styles.centered}>
                    <span
                      className={`${styles.estadoChip} ${
                        f.estado_vinculacion === "pendiente"
                          ? styles.chipPendiente
                          : f.estado_vinculacion === "vinculada"
                            ? styles.chipVinculada
                            : styles.chipIgnorada
                      }`}
                    >
                      {f.estado_vinculacion}
                    </span>
                  </td>
                  <td className={styles.centered}>
                    {f.estado_vinculacion === "pendiente" && f._dir === "emitida" && (
                      <button className={styles.btnAccion} onClick={() => setModalEmitida(f)}>
                        Vincular
                      </button>
                    )}
                    {f.estado_vinculacion === "pendiente" && f._dir === "recibida" && (
                      <button className={styles.btnAccion} onClick={() => setModalRecibida(f)}>
                        Asignar
                      </button>
                    )}
                    {f.estado_vinculacion !== "pendiente" && f._dir === "emitida" && (
                      <button className={styles.btnAccionOutline} onClick={() => setModalEmitida(f)}>
                        Editar
                      </button>
                    )}
                    {f.estado_vinculacion !== "pendiente" && f._dir === "recibida" && (
                      <button className={styles.btnAccionOutline} onClick={() => setModalRecibida(f)}>
                        Editar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <VincularEmitidaModal
        isOpen={!!modalEmitida}
        toggle={() => setModalEmitida(null)}
        factura={modalEmitida}
        onVinculada={cargarDatos}
      />
      <AsignarRecibidaModal
        isOpen={!!modalRecibida}
        toggle={() => setModalRecibida(null)}
        factura={modalRecibida}
        onAsignada={cargarDatos}
      />
    </div>
  );
};

export default ConciliacionSII;
